import type { JSX, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { CatalogueApi } from '@/features/catalogue';

import type { SessionsApi } from './api';

/**
 * What the §6 session and commission screens need from outside React.
 *
 * The ability flags mirror the §5 permissions so the browser only draws what the caller
 * could actually do; the api enforces every one of them again (R14 — a gate in the browser
 * is a courtesy, never the control).
 *
 * `ownStaffId` carries the two carve-outs no permission string can express: a technician
 * reads only their own statement (R20), and only the current primary may complete a
 * session (R1). Both are decided again server-side.
 */
export interface SessionsDeps {
  api: SessionsApi;
  /**
   * The slices of BP-02's catalogue the timer screen needs: a service name for the header,
   * and the SKU list the materials sheet searches. Through the **barrel**, which is the one
   * thing BP-00 R5 allows of a sibling feature — never a file inside it.
   */
  catalogue: Pick<CatalogueApi, 'listServices' | 'listSkus' | 'skuByBarcode'>;
  /** Every technician's name, for the step, hand-over and statement screens. */
  staff: () => Promise<{ id: string; display_name: string }[]>;
  /** `session.run` — start, stop, pause, hand over, add materials, complete. */
  canRun: boolean;
  /** `session.manage` — abandon, read the event log, open someone else's session. */
  canManage: boolean;
  /** `commission.manage` — the rule editor. Owner and manager only (§5). */
  canManageCommission: boolean;
  /** The staff row of the signed-in user, when they have one. */
  ownStaffId: string | null;
  /** True when the caller may read any technician's statement (owner, manager, accountant). */
  canReadAnyStatement: boolean;
  /** A fresh `Idempotency-Key` per click (ARCHITECTURE §3 API). Injected so a test is stable. */
  newIdempotencyKey: () => string;
  /** The tenant's IANA timezone, for the wall clocks the screens draw. */
  timezone: string;
  /** Open a URL the browser should download (R20's CSV). */
  openUrl: (url: string) => void;
  /**
   * Ask the platform to keep the tablet awake (§6).
   *
   * Injected and allowed to fail: `navigator.wakeLock` is absent on desktop Safari and
   * refused when the page is hidden, and R5 makes correctness independent of it — the
   * elapsed time is a server timestamp, not a running counter the screen owns.
   */
  requestWakeLock: () => Promise<() => void>;
}

const SessionsContext = createContext<SessionsDeps | null>(null);

export function SessionsProvider({
  deps,
  children,
}: {
  deps: SessionsDeps;
  children: ReactNode;
}): JSX.Element {
  return <SessionsContext.Provider value={deps}>{children}</SessionsContext.Provider>;
}

/** The session dependencies. Throws when a screen is rendered outside the provider. */
export function useSessionsDeps(): SessionsDeps {
  const deps = useContext(SessionsContext);
  if (deps === null) {
    throw new Error('26AD sessions: this screen must be rendered inside <SessionsProvider>');
  }
  return deps;
}

/** A random `Idempotency-Key`; `crypto.randomUUID` exists in every target browser. */
export function randomIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * The real wake lock, and a release that is safe to call twice.
 *
 * Everything here is optional at runtime: an older tablet has no `wakeLock`, and a request
 * made while the page is hidden rejects. Both end as a no-op release, because §6 says the
 * screen asks — it never depends on the answer.
 */
export function browserWakeLock(): () => Promise<() => void> {
  return async () => {
    const lock = (
      navigator as {
        wakeLock?: {
          request: (kind: 'screen') => Promise<{ release: () => Promise<void> }>;
        };
      }
    ).wakeLock;
    if (lock === undefined) {
      return () => undefined;
    }
    try {
      const sentinel = await lock.request('screen');
      return () => void sentinel.release().catch(() => undefined);
    } catch {
      return () => undefined;
    }
  };
}
